import { useTranslation } from 'next-i18next'
import React, { useCallback, useRef, useState } from 'react'
import BaseButton from 'src/components/Buttons/BaseButton'
import CodeInput from 'src/components/Login/CodeInput'
import useUploadFiles from 'src/hooks/useUploadFiles'
import {
  DirectoryClaimCodeChannel,
  DirectoryClaimEvidenceKind,
  IDirectoryClaimSelf
} from 'src/types/graphql'
import { ACCESSIBLE_BLUE, ACCESSIBLE_RED, FOCUS_RING } from './claimTheme'

interface ClaimEvidenceProps {
  claim: IDirectoryClaimSelf
  busy: boolean
  codeSentTo: string | null
  error: string | null
  onSendCode: (channel: DirectoryClaimCodeChannel) => void
  onVerifyCode: (code: string) => void
  onAttachDocument: (document: {
    id: string
    name: string
    url: string
  }) => void
  onSubmit: () => void
}

/**
 * Literal key map — `t()` keys are typed, so `t(`CLAIM.EVIDENCE_${kind}`)` is
 * a compile error. The upside of the verbosity: a locale key deleted out from
 * under this map fails the build instead of rendering a raw key.
 */
const EVIDENCE_LABEL: Record<
  DirectoryClaimEvidenceKind,
  | 'CLAIM.EVIDENCE_PHONE_CODE'
  | 'CLAIM.EVIDENCE_EMAIL_CODE'
  | 'CLAIM.EVIDENCE_DOMAIN_MATCH'
  | 'CLAIM.EVIDENCE_DOCUMENT'
> = {
  [DirectoryClaimEvidenceKind.PhoneCode]: 'CLAIM.EVIDENCE_PHONE_CODE',
  [DirectoryClaimEvidenceKind.EmailCode]: 'CLAIM.EVIDENCE_EMAIL_CODE',
  [DirectoryClaimEvidenceKind.DomainMatch]: 'CLAIM.EVIDENCE_DOMAIN_MATCH',
  [DirectoryClaimEvidenceKind.Document]: 'CLAIM.EVIDENCE_DOCUMENT'
}

/**
 * Step 2 of the claim: the evidence ladder (plan §3.1), strongest rung first.
 *
 * Everything here is OPTIONAL. A human reviews every claim either way (§3.2 is
 * manual by design, matching the no-vendor KYC posture), so the ladder's job is
 * to give that human a signal — not to gate the operator out. The copy says so,
 * because an operator who thinks they cannot proceed without a document will
 * email us instead, off the audit trail.
 *
 * Codes only ever go to a destination WE already hold for the facility. The
 * masked destination is shown so the operator knows which line will ring; the
 * full value never leaves the server, or the claim flow would become a way
 * around the reveal cap.
 */
const ClaimEvidence = ({
  claim,
  busy,
  codeSentTo,
  error,
  onSendCode,
  onVerifyCode,
  onAttachDocument,
  onSubmit
}: ClaimEvidenceProps) => {
  const { t } = useTranslation('directorio')
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploadFailed, setUploadFailed] = useState(false)
  const {
    loading: uploading,
    uploadFiles,
    acceptedExtensions
  } = useUploadFiles()

  const verified = new Set(claim.evidence.map((e) => e.kind))
  const { options } = claim
  const hasCode =
    verified.has(DirectoryClaimEvidenceKind.PhoneCode) ||
    verified.has(DirectoryClaimEvidenceKind.EmailCode)

  /**
   * `uploadFiles` directly, NOT `onSelectFiles`: that helper only invokes its
   * callback when at least one file came back, so an unsupported type, a failed
   * signed-URL fetch or a 30s network timeout resolves to "nothing happened" —
   * a snackbar fires (unannounced, and in hardcoded Spanish) and this component
   * never learns. Reading the result lets the failure reach the live region.
   */
  const handleFiles = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const list = event.target.files
      event.target.value = ''
      if (!list || list.length === 0) return
      setUploadFailed(false)
      const uploaded = await uploadFiles(Array.from(list))
      const file = uploaded[0]
      if (!file) {
        setUploadFailed(true)
        return
      }
      onAttachDocument({ id: file.id, name: file.fileName, url: file.url })
    },
    [uploadFiles, onAttachDocument]
  )

  return (
    <div className="mt-5 flex flex-col gap-5">
      <p className="text-[14px] leading-relaxed text-ink-200">
        {t('CLAIM.LADDER_INTRO')}
      </p>

      {/* The state that was missing (2026-09-23): once the code verifies, the
          code section simply vanished and the operator was left looking at an
          OPTIONAL document upload with "Enviar solicitud" below it. Nothing
          said "you're done — send it". A domain+email-verified operator
          (Akiguarda, 2026-08-27) stopped exactly here and sat as a draft for
          27 days. So: a green, unmissable confirmation with its own primary
          submit, ABOVE the optional rung. */}
      {hasCode && (
        <section
          role="status"
          className="bg-green-50 rounded-xl border border-green-600/30 p-4"
        >
          <h4 className="flex items-center gap-2 text-[15px] font-semibold text-green-800">
            <i className="si si-check text-[11px]" aria-hidden="true" />
            {t('CLAIM.VERIFIED_TITLE')}
          </h4>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-700">
            {t('CLAIM.VERIFIED_BODY')}
          </p>
          <BaseButton
            variant="contained"
            customColor={ACCESSIBLE_BLUE}
            loading={busy}
            className={`mt-3 rounded-full ${FOCUS_RING}`}
            onClick={onSubmit}
          >
            {t('CLAIM.SUBMIT_CTA')}
          </BaseButton>
        </section>
      )}

      {/* Rung 1 + 2 — a code to a destination already on record. */}
      {(options.phoneMasked || options.emailMasked) && !hasCode && (
        <section className="rounded-xl border border-ink-100/15 bg-cream-100 p-4">
          <h4 className="text-[15px] font-semibold text-ink-700">
            {t('CLAIM.CODE_TITLE')}
          </h4>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-200">
            {t('CLAIM.CODE_BODY')}
          </p>
          {/* Stacked below md — two labelled controls on one row is the
              horizontal-action-group rule, and `BaseButton` hardcodes
              `white-space: nowrap`, so a long masked destination inside the
              label would overflow 320px rather than wrap. The destination sits
              OUTSIDE the label for the same reason, which also keeps the
              accessible name short and stable for voice control. */}
          <div className="mt-3 flex flex-col gap-3 md:flex-row md:gap-4">
            {options.phoneMasked && (
              <div>
                <BaseButton
                  variant="outlined"
                  disabled={busy}
                  className={`w-full rounded-full md:w-auto ${FOCUS_RING}`}
                  onClick={() => onSendCode(DirectoryClaimCodeChannel.Sms)}
                >
                  {t('CLAIM.CODE_SMS_CTA')}
                </BaseButton>
                <p className="mt-1 text-[11px] break-all text-ink-200">
                  {options.phoneMasked}
                </p>
              </div>
            )}
            {options.emailMasked && (
              <div>
                <BaseButton
                  variant="outlined"
                  disabled={busy}
                  className={`w-full rounded-full md:w-auto ${FOCUS_RING}`}
                  onClick={() => onSendCode(DirectoryClaimCodeChannel.Email)}
                >
                  {t('CLAIM.CODE_EMAIL_CTA')}
                </BaseButton>
                <p className="mt-1 text-[11px] break-all text-ink-200">
                  {options.emailMasked}
                </p>
              </div>
            )}
          </div>
          {/* An SMS to a landline is delivered to nobody and looks identical to
              a success from here — say so rather than let them wait. */}
          <p className="mt-2 text-[11px] leading-snug text-ink-200">
            {t('CLAIM.CODE_LANDLINE_HINT')}
          </p>

          {codeSentTo && (
            // `role="status"` because the field materialises with no other
            // signal that the code went out. `CodeInput` autofocuses its first
            // cell, so focus follows too.
            <div role="status" className="mt-4">
              <p className="mb-2 text-[14px] font-semibold text-black-400">
                {t('CLAIM.CODE_LABEL', { destination: codeSentTo })}
              </p>
              {/* The shared OTP primitive: six labelled cells, paste handling,
                  autofocus, and exactly-one submit per distinct code. It fires
                  on completion, so there is no separate (and previously
                  tab-order-removing, disabled) Verify button. */}
              <CodeInput onComplete={onVerifyCode} />
            </div>
          )}
        </section>
      )}

      {/* Verified rungs, so the operator can see what they have already proved. */}
      {claim.evidence.length > 0 && (
        <ul aria-live="polite" className="flex flex-col gap-1.5">
          {claim.evidence.map((item) => (
            <li
              key={item.kind}
              className="flex items-start gap-2 text-[13px] text-ink-700"
            >
              <i
                className="si si-check mt-1 text-[9px] text-blue-800"
                aria-hidden="true"
              />
              <span>
                {t(EVIDENCE_LABEL[item.kind])}
                {item.detail ? ` · ${item.detail}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Rung 3 — a document. Private bucket; `useUploadFiles` always mints a
          `nonPublic` signed URL, which is what keeps an acta constitutiva out
          of the world-readable bucket. */}
      <section className="rounded-xl border border-ink-100/15 bg-cream-100 p-4">
        <h4 className="text-[15px] font-semibold text-ink-700">
          {t('CLAIM.DOC_TITLE')}
        </h4>
        <p className="mt-1 text-[13px] leading-relaxed text-ink-200">
          {t('CLAIM.DOC_BODY')}
        </p>
        {/* `hidden`, not `sr-only`: an sr-only file input stays focusable and
            in the accessibility tree, so a keyboard user tabs into an unnamed
            1×1px control and the focus ring vanishes. Every other file input in
            this repo uses `hidden`. */}
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept={acceptedExtensions.join(',')}
          onChange={handleFiles}
        />
        <BaseButton
          variant="outlined"
          loading={uploading}
          className={`mt-3 rounded-full ${FOCUS_RING}`}
          onClick={() => fileRef.current?.click()}
        >
          {t('CLAIM.DOC_CTA')}
        </BaseButton>
        {/* The upload can take up to 30s and had no announcement at any stage —
            not while running, not on success, and not on the failure path where
            `uploadFiles` returns an empty array. */}
        <p aria-live="polite" className="mt-2 text-[12px] text-ink-200">
          {uploading
            ? t('CLAIM.DOC_UPLOADING')
            : claim.documentCount > 0
              ? t('CLAIM.DOC_COUNT', { count: claim.documentCount })
              : ''}
        </p>
        {uploadFailed && (
          <p
            role="alert"
            className="text-[12px]"
            style={{ color: ACCESSIBLE_RED }}
          >
            {t('CLAIM.DOC_FAILED')}
          </p>
        )}
      </section>

      {error && (
        <p role="alert" className="text-xs" style={{ color: ACCESSIBLE_RED }}>
          {error}
        </p>
      )}

      <div>
        <BaseButton
          variant="contained"
          customColor={ACCESSIBLE_BLUE}
          loading={busy}
          className={`rounded-full ${FOCUS_RING}`}
          onClick={onSubmit}
        >
          {t('CLAIM.SUBMIT_CTA')}
        </BaseButton>
        <p className="mt-2 text-[11px] leading-snug text-ink-200">
          {t('CLAIM.SUBMIT_NOTE')}
        </p>
      </div>
    </div>
  )
}

export default React.memo(ClaimEvidence)
